import type { ComponentType, PropsWithChildren } from 'react';
import React, { Fragment } from 'react';

import { PRODUCT_HOME_ID } from '@confluence/app-navigation/entry-points/navigationIDs.ts';
import { ExternalSharePageLoader } from '@confluence/external-share/entry-points/ExternalSharePage.ts';
import { expVal } from '@confluence/feature-experiments';
import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { getUniquePageLoadId } from '@confluence/unique-page-load-id/entry-points/UniquePageLoadId.ts';

type AccessLinkRouteComponent = {
	({ params: { shareToken }, hash }: RouteMatch): React.JSX.Element;
	NAVIGATION_PARAMS({ name }: RouteMatch): AccessLinkRouteNavigationParams;
};

type AccessLinkRouteNavigationParams = {
	Screen: {
		screenEvent: {
			name: string;
			attributes: {
				isPublicView: boolean;
				isAccessLinkView: boolean;
				pageLoadInfo: ReturnType<typeof getUniquePageLoadId>;
			};
		};
		pageState: {
			routeName: string;
		};
	};
	MainLayout: {
		navView: string;
	};
};

export function getAccessLinkRoute({
	ContextProvider = Fragment,
	NotFound = Fragment,
	ClassicEditorContextProviderLoader,
}: GetAccessLinkRouteArgs = {}): AccessLinkRouteComponent {
	function AccessLinkRoute({ params: { shareToken }, hash }: RouteMatch): React.JSX.Element {
		if (!expVal('shareable_links_m0', 'isEnabled', false)) {
			return <NotFound />;
		}

		return (
			<ContextProvider>
				<ExternalSharePageLoader
					shareToken={shareToken}
					queryHash={hash}
					ClassicEditorContextProviderLoader={ClassicEditorContextProviderLoader}
				/>
			</ContextProvider>
		);
	}

	AccessLinkRoute.NAVIGATION_PARAMS = ({ name }: RouteMatch): AccessLinkRouteNavigationParams => ({
		Screen: {
			screenEvent: {
				name: 'viewPageScreen',
				attributes: {
					isPublicView: false,
					isAccessLinkView: true,
					pageLoadInfo: getUniquePageLoadId(),
				},
			},
			pageState: {
				routeName: name,
			},
		},
		MainLayout: {
			navView: PRODUCT_HOME_ID,
		},
	});

	return AccessLinkRoute;
}

type GetAccessLinkRouteArgs = {
	ContextProvider?: ComponentType<PropsWithChildren<{}>>;
	NotFound?: ComponentType;
	/**
	 * Extension-v1 (legacy macro) context for the live editor a live doc mounts. Injected by the
	 * classic route for the same reason `ContextProvider` is; `AccessLinkRouteForSSR` leaves it
	 * undefined, matching what SSR already does on the normal view-page route.
	 */
	ClassicEditorContextProviderLoader?: ComponentType<PropsWithChildren<{}>>;
};

export const AccessLinkRouteForSSR: AccessLinkRouteComponent = getAccessLinkRoute();
